import * as React from 'react';
import pluralize from 'pluralize';
import { once } from 'lodash';

// @ts-ignore
import AdminSchoolSisDashboardIssueTable from 'admin/components/AdminSchoolSisDashboardIssueTable';
// @ts-ignore
import AdminSchoolSisDashboardSummary from 'admin/components/AdminSchoolSisDashboardSummary';
// @ts-ignore
import AdminSchoolSisSyncHeader from 'admin/components/AdminSchoolSisSyncHeader';
import EventTracking from 'lib/EventTracking';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
// @ts-ignore
import AdminSisSyncNotificationOptions from 'admin/components/AdminSisSyncNotificationOptions';
// @ts-ignore
import { SisSyncOverview, IssueSummary } from 'admin/components/AdminSchoolSisDashboard';

type Props = {
  orgUuid: string;
  orgId: number;
  sisSyncOverview: SisSyncOverview;
};

export default class AdminSisSyncConnectedView extends React.Component<Props> {
  componentWillMount() {
    this.trackView();
  }

  renderNotificationOptions() {
    const { orgUuid } = this.props;
    const { canNotifyTeachers, canNotifyStudents, canNotifyParents } = this.props.sisSyncOverview;
    const canNotify = canNotifyTeachers || canNotifyStudents || canNotifyParents;
    return (
      canNotify && (
        <Row>
          <AdminSisSyncNotificationOptions
            orgUuid={orgUuid}
            canNotifyTeachers={canNotifyTeachers}
            canNotifyStudents={canNotifyStudents}
            canNotifyParents={canNotifyParents}
          />
        </Row>
      )
    );
  }

  renderHeader() {
    const {
      lastSyncTimestamp,
      parentCount,
      studentCount,
      teacherCount,
    } = this.props.sisSyncOverview;

    const subtext = (
      <Text>
        There are <Text weight="semibold">{pluralize('teacher', teacherCount, true)}</Text>,{' '}
        <Text weight="semibold">{pluralize('student', studentCount, true)}</Text>, and{' '}
        <Text weight="semibold">{pluralize('parent', parentCount, true)}</Text> added from your SIS.
      </Text>
    );

    return (
      <AdminSchoolSisSyncHeader
        subtext={subtext}
        status="connected"
        lastSyncTimestamp={lastSyncTimestamp}
      />
    );
  }
  renderSummary() {
    const {
      orgUuid,
      sisSyncOverview: { errorCount, alertCount, allIssuesDownloadUrl },
    } = this.props;

    return (
      <Row>
        <AdminSchoolSisDashboardSummary
          errors={errorCount}
          alerts={alertCount}
          csvUrl={allIssuesDownloadUrl}
          orgUuid={orgUuid}
        />
      </Row>
    );
  }

  renderErrorsTable() {
    const {
      orgUuid,
      sisSyncOverview: { errorCount, issueSummaries },
    } = this.props;

    if (errorCount === 0) {
      return null;
    }

    const subtext = (
      <Text color="thunder">
        There are errors in your SIS that are preventing user accounts or classes from being
        created. Read about them below and fix them directly in your SIS.{' '}
        <Link href={LinkConstants.UPDATE_SIS} weight="semibold" target="_blank">
          Learn more about how to resolve errors and alerts here.
        </Link>
      </Text>
    );

    return (
      <Row>
        <AdminSchoolSisDashboardIssueTable
          type="Error"
          subtext={subtext}
          orgUuid={orgUuid}
          issues={issueSummaries.filter((i: IssueSummary) => i.severity === 'error')}
        />
      </Row>
    );
  }

  renderAlertsTable() {
    const {
      orgUuid,
      sisSyncOverview: { alertCount, issueSummaries },
    } = this.props;

    if (alertCount === 0) {
      return null;
    }

    const subtext = (
      <Text color="thunder">
        Alerts are minor data errors in your SIS that, if fixed, would make Remind more useful and
        productive to use. We recommend you address them in your SIS to fully enjoy using Remind.{' '}
        <Link href={LinkConstants.UPDATE_SIS} weight="semibold" target="_blank">
          Learn more about how to resolve errors and alerts here.
        </Link>
      </Text>
    );

    return (
      <Row>
        <AdminSchoolSisDashboardIssueTable
          type="Alert"
          subtext={subtext}
          orgUuid={orgUuid}
          issues={issueSummaries.filter((i: IssueSummary) => i.severity === 'alert')}
        />
      </Row>
    );
  }

  renderNoErrorsInfo() {
    const {
      orgId,
      sisSyncOverview: { errorCount, alertCount },
    } = this.props;
    const slug = `/organizations/${orgId}`;

    if (alertCount + errorCount !== 0) {
      return null;
    }

    return (
      <Text>
        There were no problems with your most recent sync. If you’d like to check how rostering is
        shaping Remind, check the{' '}
        <Link to={`${slug}/people`} weight="semibold">
          People
        </Link>{' '}
        and{' '}
        <Link to={`${slug}/classes`} weight="semibold">
          Classes
        </Link>{' '}
        views for new people and classes.
      </Text>
    );
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.connected.viewed', {
      organization_uud: this.props.orgUuid,
    });
  });

  render() {
    return (
      <section className="AdminSisSyncConnectedView">
        {this.renderHeader()}
        {this.renderNotificationOptions()}
        {this.renderSummary()}
        {this.renderErrorsTable()}
        {this.renderAlertsTable()}
        {this.renderNoErrorsInfo()}
      </section>
    );
  }
}
